import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import './Pill.css'
import cn from 'classnames'
import { v4 } from 'uuid'
import { IPill, PillStatus, PillVariant } from './type'
import PillItem from './PillItem'
import { DragDropContext, Droppable } from 'react-beautiful-dnd'
import { reorder, setAttributeCollectionOfElement, setCaretPos } from '../../utils/index'
import { usePrevious } from '../../utils/hooks'
import { TruncateProps } from 'components/Truncate/Truncate'
import Icon from '../Icon2/Icon'

const focusLastEditablePill = (parentNode) => {
  const inputNodes = parentNode.getElementsByTagName('INPUT') as HTMLCollectionOf<HTMLInputElement>

  if (parentNode.tabIndex === 0) {
    parentNode.setAttribute('tabindex', '-1')
    setAttributeCollectionOfElement(inputNodes, 'tabindex', '0')
  }

  if (inputNodes.length) {
    const lastInputEle = inputNodes[inputNodes.length ? inputNodes.length - 1 : 0]
    if (lastInputEle) {
      lastInputEle.tabIndex = 0
      setCaretPos(lastInputEle, lastInputEle.value.length)
    }
  }
}

const focusFirstEditablePill = (parentNode, addNewPillItem) => {
  const inputNodes = parentNode.getElementsByTagName('INPUT') as HTMLCollectionOf<HTMLInputElement>
  parentNode.tabIndex = -1
  setAttributeCollectionOfElement(inputNodes, 'tabindex', '0')
  if (inputNodes.length) {
    const firstInputEle = inputNodes[0]
    if (firstInputEle) {
      setCaretPos(firstInputEle, firstInputEle.value.length)
    }
  }
  if (!inputNodes.length) addNewPillItem()
}

interface PillProps {
  background?: string
  status?: PillStatus
  variant?: PillVariant
  id?: string
  testId?: string
  isDisabled?: boolean
  items: IPill[]
  isEditable?: boolean
  onChange?: (data: IPill[]) => void
  isSortable?: boolean
  placeholder?: string
  shouldHaveBorder?: boolean
  stroke?: string
  onPillClickHandler?: Function
  customKeyDown?: Function
  isSingleton?: boolean
  maxContainerWidth?: number
  canTruncate?: boolean
  className?: string
  isError?: boolean
  size?: 'default' | 'small'
  highlightText?: string
  dropDownContent?: (closeDropdown: () => void) => ReactNode
  truncateOptions?: TruncateProps;
  highlightAiSuggested?: boolean;
  isResizeableContainer?: boolean;
  maxContainerHeight?: number;
  isResponsive?: boolean;
  minContainerHeight?: number;
  enableBulkPaste?: boolean;
  bulkPasteSeparators?: string | string[];
}

const Pill: React.FC<PillProps> = (props) => {

  const {
    variant = "label",
    id = "cs-pill",
    testId = "cs-pill",
    isDisabled = false,
    items = [],
    status,
    isEditable,
    onChange,
    isSortable,
    placeholder,
    background,
    shouldHaveBorder = true,
    stroke,
    onPillClickHandler,
    isSingleton,
    maxContainerWidth,
    maxContainerHeight = 300,
    canTruncate,
    className,
    isError,
    size = 'default',
    highlightText,
    dropDownContent,
    truncateOptions,
    highlightAiSuggested = false,
    isResizeableContainer = false,
    isResponsive = false,
    minContainerHeight = 100,
    enableBulkPaste = false,
    bulkPasteSeparators = [',', ';', '\n']
  } = props

  const [pillStyle, setPillStyle] = useState({
    width: `${maxContainerWidth}px`,
    maxHeight: `${minContainerHeight}px`,
  })
  const dropStateRef = useRef<{ isDragging: boolean }>({ isDragging: false })
  const pillWrapRef = useRef<HTMLDivElement>()
  const prevItems = usePrevious(items)

  const onItemChange = (updateItemData: Omit<IPill, 'id'>, index: number) => {
    const newItem = [...items]
    const newUpdatedData = { ...updateItemData, id: items[index].id }
    newItem[index] = newUpdatedData
    onChange(newItem)
  }

  const handleNewItemAdd = useCallback(() => {
    if (dropStateRef.current.isDragging) return
    if (items.length && items[items.length - 1].text === '') return
    const newItem = [...items]
    newItem.push({ id: v4().split('-').join(''), text: '' })
    onChange(newItem)
  }, [items, onChange, isEditable])

  const handleItemRemove = (index: number) => {
    const newItem = [...items]
    newItem.splice(index, 1)
    onChange(newItem)
  }

  const handleDragEnd = (result) => {
    dropStateRef.current.isDragging = false

    if (!result?.destination) return
    if (result?.source?.index === result?.destination?.index) return
    onChange(reorder(items, result.source.index, result.destination.index))
  }

  const handleDragStart = () => {
    dropStateRef.current.isDragging = true
  }
  useEffect(() => {
    if (isEditable) {
      const keyUpListener = (event) => {
        if (event.code === 'Space' || event.code === 'Enter') {
          if (event.code === 'Enter') event.preventDefault();
          focusFirstEditablePill(pillWrapRef.current, handleNewItemAdd)
        }
      }
      const focusListener = () => {
        document.addEventListener('keyup', keyUpListener)
      }
      const blurListener = () => {
        document.removeEventListener('keyup', keyUpListener)
      }
      pillWrapRef?.current?.addEventListener('focus', focusListener)
      pillWrapRef?.current?.addEventListener('blur', blurListener)
      return () => {
        document.removeEventListener('keyup', keyUpListener)
        pillWrapRef?.current?.removeEventListener('focus', focusListener)
        pillWrapRef?.current?.removeEventListener('blur', focusListener)
      }
    }
    return () => { }
  }, [pillWrapRef.current, handleNewItemAdd, isEditable])

  useEffect(() => {
    if (isEditable && enableBulkPaste) {
      const handlePaste = (event: ClipboardEvent) => {
        const target = event.target as HTMLElement;
        if (target.tagName !== 'INPUT') return;
        
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        
        const pastedText = event.clipboardData?.getData('text') || '';
        if (!pastedText.trim()) return;

        const separators = Array.isArray(bulkPasteSeparators) 
          ? bulkPasteSeparators 
          : [bulkPasteSeparators];
        
        // Handle empty separator array - treat entire paste as single item
        if (separators.length === 0) return;
          
        const escapedSeparators = separators.map(sep => 
          sep.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        );
        // Use alternation instead of character class to avoid edge cases
        // with hyphens, carets, and other position-dependent special chars
        const splitPattern = new RegExp(escapedSeparators.join('|'));

        const pastedItems = pastedText
          .split(splitPattern)
          .map(item => item.trim())
          .filter(item => item.length > 0);

        if (pastedItems.length === 0) return;

        (target as HTMLInputElement).value = '';

        const newPills = pastedItems.map(text => ({ 
          id: v4().split('-').join(''), 
          text 
        }));
        
        const updatedItems = [...items, ...newPills];
        onChange(updatedItems);
      };

      // Capture current element to avoid stale closure in cleanup
      const element = pillWrapRef.current;
      if (element) {
        element.addEventListener('paste', handlePaste, true);
        
        return () => {
          element.removeEventListener('paste', handlePaste, true);
        };
      }
    }
    return () => { }
  }, [isEditable, enableBulkPaste, bulkPasteSeparators, items, onChange])

  const keyDownHandler = (e: any) => {
    if (items?.length === 0) {
      handleNewItemAdd()
    }
    e.target.classList.contains("Pill") && props.customKeyDown && props.customKeyDown(e)
  }

  useEffect(() => {
    if (isEditable) {
      const prevItemCount = prevItems?.length || 0
      const latestItemCount = items?.length || 0
      if (prevItems && latestItemCount > prevItemCount) {
        focusLastEditablePill(pillWrapRef.current)
      }
    }
  }, [items?.length, isEditable])

  const wrapClassName = cn(
    'Pill',
    `Pill__${variant}`,
    { [`Pill__disabled`]: isDisabled },
    { [`Pill--border`]: shouldHaveBorder },
    { ['Pill--single']: items.length === 1 },
    { [`Pill--small`]: size === 'small' },
    className,
    { [`Pill--inputError`]: isError },
    { [`Pill--isResizeableContainer`]: isResizeableContainer }
  )

  let y = 0, h = 0

  const mouseUpHandler = () => {
    // Remove the handlers of `mousemove` and `mouseup`
    document.removeEventListener('mousemove', mouseMoveHandler)
    document.removeEventListener('mouseup', mouseUpHandler)
  }

  const mouseMoveHandler = (e: { clientY: number }) => {
    if (pillStyle.maxHeight !== `${maxContainerHeight}px`) {
      setPillStyle({
        ...pillStyle,
        maxHeight: `${maxContainerHeight}px`,
      })
    }
    const ele = document.getElementById(id)
    ele.focus()
    const dy = e.clientY - y
    const finalHeight = h + dy
    if (finalHeight > 40) {
      ele.style.height = `${h + dy}px`
    } else {
      ele.style.height = '40px'
    }
  }

  const mouseDownHandler = (e: { clientY: number }) => {
    const ele = document.getElementById(id)
    y = e.clientY

    // calculate the dimension of element
    const styles = getComputedStyle(ele)
    let height = parseInt(styles?.height || '0')
    if (height > 40) {
      h = height
    } else {
      h = 40
    }
    // attach the listeners to `document`
    document.addEventListener('mousemove', mouseMoveHandler)
    document.addEventListener('mouseup', mouseUpHandler)
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
      <Droppable droppableId="PillDroppable" direction="horizontal" isDropDisabled={!isSortable || isDisabled}>
        {(provided) => (
          !isResizeableContainer ?
            <div
              {...provided.droppableProps}
              ref={(el) => {
                provided.innerRef(el)
                pillWrapRef.current = el
              }}
              className={wrapClassName}
              id={id}
              data-test-id={testId}
              tabIndex={shouldHaveBorder ? 0 : undefined}
              onClick={isEditable && !isDisabled ? handleNewItemAdd : undefined}
              onKeyDown={keyDownHandler}
            >
              {!items.length && placeholder ? (
                <div className="Pill__placeholder">{placeholder}</div>
              ) : (
                items.map((eachItem, index) => {
                  const { text, leadingIcon, trailingIcon, id, badgeText, isSelected, isPillDisabled, isAiSuggested, isError } = eachItem
                  return (
                    <PillItem
                      key={id}
                      itemId={id}
                      index={index}
                      text={text}
                      leadingIcon={leadingIcon}
                      trailingIcon={trailingIcon}
                      variant={variant}
                      status={status}
                      isDisabled={isDisabled}
                      isEditable={isEditable}
                      onChangeItem={isEditable ? onItemChange : undefined}
                      onNewItemAdd={isEditable ? handleNewItemAdd : undefined}
                      onRemoveItem={isEditable ? handleItemRemove : undefined}
                      isSortable={isSortable}
                      background={background}
                      stroke={stroke}
                      onPillClickHandler={onPillClickHandler}
                      customKeyDown={props.customKeyDown}
                      isLastTag={items.length - 1 === index}
                      isSingleton={isSingleton}
                      pillsCount={items.length}
                      canTruncate={canTruncate}
                      badgeText={badgeText}
                      highlightText={highlightText}
                      dropDownContent={dropDownContent}
                      isSelected={isSelected}
                      isPillDisabled={isPillDisabled}
                      truncateOptions={truncateOptions}
                      isAiSuggested={isAiSuggested}
                      isError={isError}
                      highlightAiSuggested={highlightAiSuggested}
                      isResponsive={isResponsive}
                    />
                  )
                })
              )}
              {provided.placeholder}
            </div> :
            <div className="Pill__wrap" style={pillStyle}>
              <div
                {...provided.droppableProps}
                ref={(el) => {
                  provided.innerRef(el)
                  pillWrapRef.current = el
                }}
                className={wrapClassName}
                id={id}
                style={pillStyle}
                data-test-id={testId}
                tabIndex={shouldHaveBorder ? 0 : undefined}
                onClick={isEditable && !isDisabled ? handleNewItemAdd : undefined}
                onKeyDown={keyDownHandler}
              >
                {!items.length && placeholder ? (
                  <div className="Pill__placeholder">{placeholder}</div>
                ) : (
                  items.map((eachItem, index) => {
                    const { text, leadingIcon, trailingIcon, id, badgeText, isSelected, isPillDisabled, isAiSuggested, isError } = eachItem
                    return (
                      <PillItem
                        key={id}
                        itemId={id}
                        index={index}
                        text={text}
                        leadingIcon={leadingIcon}
                        trailingIcon={trailingIcon}
                        variant={variant}
                        status={status}
                        isDisabled={isDisabled}
                        isEditable={isEditable}
                        onChangeItem={isEditable ? onItemChange : undefined}
                        onNewItemAdd={isEditable ? handleNewItemAdd : undefined}
                        onRemoveItem={isEditable ? handleItemRemove : undefined}
                        isSortable={isSortable}
                        background={background}
                        stroke={stroke}
                        onPillClickHandler={onPillClickHandler}
                        customKeyDown={props.customKeyDown}
                        isLastTag={items.length - 1 === index}
                        isSingleton={isSingleton}
                        pillsCount={items.length}
                        canTruncate={canTruncate}
                        badgeText={badgeText}
                        highlightText={highlightText}
                        dropDownContent={dropDownContent}
                        isSelected={isSelected}
                        isPillDisabled={isPillDisabled}
                        truncateOptions={truncateOptions}
                        isAiSuggested={isAiSuggested}
                        isError={isError}
                        highlightAiSuggested={highlightAiSuggested}
                        isResponsive={isResponsive}
                      />
                    )
                  })
                )}
                {provided.placeholder}
              </div>
              {isResizeableContainer && <div className="Pill__resize-icon" onMouseDown={mouseDownHandler}>
                <Icon icon="FieldResize" />
              </div>
              }
            </div>
        )}
      </Droppable>
    </DragDropContext>
  )
}

export default Pill